import { useState, CSSProperties } from "react";
import { useStore } from "reactflow";
import { useAuthStore } from "../../../store/storeAuth";
import { alpha } from "@mui/material";
import { shallow } from "zustand/shallow";
import {
    UseConditionHandleProps,
    DEFAULT_ACTIVE_COLOR,
    INVALID_SOURCE_HANDLES,
    DASHED_BORDER,
} from "./types.d";

export const useConditionHandle = ({
    isActive = false,
    activeColor,
    type,
}: UseConditionHandleProps) => {
    const { user } = useAuthStore((state) => state);
    const [isHovered, setIsHovered] = useState(false);

    const { isDraggingConnection, sourceHandle } = useStore(
        (s) => ({
            isDraggingConnection: s.connectionNodeId !== null,
            sourceHandle: {
                handleId: s.connectionStartHandle?.handleId,
                type: s.connectionStartHandle?.type,
            },
        }),
        shallow,
    );

    const isSameType = sourceHandle.type === type;
    const isInvalidSource =
        !!sourceHandle.handleId &&
        INVALID_SOURCE_HANDLES.some(handle => sourceHandle.handleId?.startsWith(handle));

    const isDark = user?.theme === "dark";
    const isDropTarget =
        isDraggingConnection && isHovered && !isInvalidSource && !isSameType;

    const resolvedColor = activeColor || DEFAULT_ACTIVE_COLOR;

    const backgroundColor = isDropTarget
        ? resolvedColor
        : isActive
            ? isDark ? "#1D2939" : "#E7F0FF"
            : isDark ? "#344054" : "#EAECF0";

    const baseStyle: CSSProperties = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "18px",
        height: "18px",
        backgroundColor,
        backgroundImage: isActive || isDropTarget ? "inherit" : DASHED_BORDER,
        border: isActive ? `1px solid ${resolvedColor}` : "none",
        boxShadow: isDropTarget
            ? `0 0 0 4px ${alpha(resolvedColor, 0.25)}, 0 0 12px ${alpha(resolvedColor, 1)}`
            : undefined,
        transition: "all 100ms ease",
        transform: `translateY(-9px) scale(${isDropTarget ? "1.15" : "1"})`,
        ...(isDraggingConnection && (isInvalidSource || isSameType) && {
            cursor: "not-allowed",
        }),
    };

    const handleMouseEnter = () => {
        if (isDraggingConnection && !isSameType) setIsHovered(true);
    };

    const handleMouseLeave = () => setIsHovered(false);

    return {
        isDropTarget,
        baseStyle,
        handleMouseEnter,
        handleMouseLeave,
    };
};
